/**
 * Portal Aktenwächter — PB-Client für `portal_akten` + `portal_akten_checklist`.
 * Pattern analog `verfahrensmotor/portalFristen.ts` (direkter PB-Pfad, Mandanten-Token).
 */

import { getPortalPocketBaseUrl } from '../../constants';
import {
  getAktenChecklistTemplate,
  VERTRAG_BERATUNG_CHECKLIST,
} from './aktenTemplates';
import type { AktenGuardProgress } from './aktenTemplates';
import {
  formatPortalAktenPbError,
  isExpectedPortalAktenKanzleiDeny,
  logPortalError,
  PORTAL_AKTEN_COLLECTION_MISSING_DE,
} from './portalErrorMessages';
import type {
  PortalAkteRecord,
  PortalAkteMemberAccess,
  PortalAkteMemberRecord,
  PortalAktenApiResult,
  PortalAktenChecklistRecord,
  PortalBoundAkte,
  PortalAktenChecklistStatus,
  KanzleiSeatForAktePicker,
} from './portalAktenTypes';
import { portalDocumentRelativeFolder, sanitizePortalFolderInput } from './portalDocuments';
import {
  findPortalAkteRecordForPath,
  matchesPortalAkteNcPath,
  portalAkteIdFromNcPath,
  portalAkteNcPathIsForeign,
} from './portalAktePath';
import { portalAkteTargetFolder } from './portalAkteDocumentActions';
import { readSeatElementEnabled, readSeatMatrixUserId } from './elementSeatInvite';
import {
  buildPortalAktenPbFilter,
  escapePortalAktenPbFilterString,
  escapePortalAktenPbFilterString as escapePbFilterString,
} from './portalAktenFilter';

export {
  expectedSameOrgAktePatchHttp,
  portalAkteAccessibleByCaller,
} from './portalAkteOrgGuard';

/** Fortschritt direkt aus PB-Checkliste (inkl. eigener Punkte). */
export function computeAktenGuardProgressFromChecklist(
  items: PortalAktenChecklistRecord[],
): AktenGuardProgress {
  let requiredTotal = 0;
  let requiredDone = 0;
  let optionalTotal = 0;
  let optionalDone = 0;

  for (const item of items) {
    const done = item.status === 'done' || item.status === 'waived';
    if (item.required) {
      requiredTotal += 1;
      if (done) requiredDone += 1;
    } else {
      optionalTotal += 1;
      if (done) optionalDone += 1;
    }
  }

  return {
    requiredTotal,
    requiredDone,
    optionalTotal,
    optionalDone,
    allRequiredDone: requiredTotal > 0 && requiredDone === requiredTotal,
  };
}

export const PORTAL_AKTEN_COLLECTION = 'portal_akten';
export const PORTAL_AKTEN_MEMBERS_COLLECTION = 'portal_akten_members';
export const PORTAL_AKTEN_CHECKLIST_COLLECTION = 'portal_akten_checklist';
export const PORTAL_AKTEN_PB_LIST_SORT = '-id';
export const PORTAL_AKTEN_CHECKLIST_SORT = 'sort_order';

export function portalAktenPanelExpandedStorageKey(customerId: string): string {
  return `arkorbit-portal-akte-expanded:${customerId.trim() || 'unknown'}`;
}

/** Erstbesuch offen; danach gilt der letzte Zustand pro angemeldetem Sitz. */
export function loadPortalAktenPanelExpanded(customerId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(portalAktenPanelExpandedStorageKey(customerId)) !== '0';
  } catch {
    return true;
  }
}

export function savePortalAktenPanelExpanded(customerId: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      portalAktenPanelExpandedStorageKey(customerId),
      expanded ? '1' : '0',
    );
  } catch {
    /* localStorage blockiert oder voll — offen/geschlossen bleibt nur für diese Session. */
  }
}

export function portalAktenChecklistExpandedStorageKey(customerId: string): string {
  return `arkorbit-portal-akte-checklist-expanded:${customerId.trim() || 'unknown'}`;
}

/** Nach erstem Anlegen eingeklappt; Nutzer-Toggle bleibt pro Sitz. */
export function loadPortalAktenChecklistExpanded(customerId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(portalAktenChecklistExpandedStorageKey(customerId));
    if (raw === null) return false;
    return raw !== '0';
  } catch {
    return false;
  }
}

export function savePortalAktenChecklistExpanded(customerId: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      portalAktenChecklistExpandedStorageKey(customerId),
      expanded ? '1' : '0',
    );
  } catch {
    /* localStorage blockiert oder voll — offen/geschlossen bleibt nur für diese Session. */
  }
}

export function portalAkteDocsPanelExpandedStorageKey(customerId: string): string {
  return `arkorbit_portal_akte_docs_panel_v1__${customerId.trim() || 'unknown'}`;
}

/** Erstbesuch aufgeklappt; Nutzer-Toggle bleibt pro Sitz. „Akte öffnen“ klappt einmal auf. */
export function loadPortalAkteDocsPanelExpanded(customerId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(portalAkteDocsPanelExpandedStorageKey(customerId));
    if (raw === null) return true;
    return raw !== '0';
  } catch {
    return true;
  }
}

export function savePortalAkteDocsPanelExpanded(customerId: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      portalAkteDocsPanelExpandedStorageKey(customerId),
      expanded ? '1' : '0',
    );
  } catch {
    /* localStorage blockiert oder voll — offen/geschlossen bleibt nur für diese Session. */
  }
}

export function portalBoundAkteStorageKey(customerId: string): string {
  return `arkorbit_portal_bound_akte_v1__${customerId.trim() || 'unknown'}`;
}

export function toPortalBoundAkte(akte: { id: string; title: string; nc_path?: string | null }): PortalBoundAkte {
  return {
    id: akte.id,
    title: akte.title,
    nc_path: akte.nc_path ?? null,
  };
}

export function loadPortalBoundAkte(customerId: string): PortalBoundAkte | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(portalBoundAkteStorageKey(customerId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const rec = parsed as Partial<PortalBoundAkte>;
    const id = typeof rec.id === 'string' ? rec.id.trim() : '';
    const title = typeof rec.title === 'string' ? rec.title.trim() : '';
    if (!id || !title) return null;
    return {
      id,
      title,
      nc_path: typeof rec.nc_path === 'string' ? rec.nc_path : rec.nc_path === null ? null : undefined,
    };
  } catch {
    return null;
  }
}

export function savePortalBoundAkte(customerId: string, akte: PortalBoundAkte | null): void {
  if (typeof window === 'undefined') return;
  try {
    const key = portalBoundAkteStorageKey(customerId);
    if (!akte?.id?.trim()) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(toPortalBoundAkte(akte)));
  } catch {
    /* localStorage blockiert oder voll — Binding bleibt nur für diese Session. */
  }
}

/** Firma-PB ohne Akten-Collections: nach erstem 404 keine weiteren GETs (Demo-Network-Noise). */
let portalAktenCollectionMissing = false;

function skipPortalAktenPbFetch(): boolean {
  if (portalAktenCollectionMissing) return true;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.trim().toLowerCase();
  // portal_akten ist Labor-Feature; Firma-Demo-PB hat die Collection typisch nicht → 404.
  return host === 'portal.arkorbit.at' || host === 'demo.arkorbit.at';
}

function normalizePbAuthHeader(authToken: string): string {
  const t = authToken.trim();
  if (!t) return '';
  return t.toLowerCase().startsWith('bearer ') ? t : `Bearer ${t}`;
}

export { buildPortalAktenPbFilter };

export {
  effectivePortalAkteNcPath,
  findPortalAkteRecordForPath,
  matchesPortalAkteNcPath,
  ncPathForNewPortalAkte,
  normalizePortalAktePath,
  portalAkteIdFromNcPath,
  portalAkteNcPathIsForeign,
  portalDokumentBelongsToAkte,
  portalOpenMetaBelongsToAkte,
} from './portalAktePath';

/** Relativer NC-Ordner einer Akte (Drop-Fix / Speichern). Leer = noch kein Ordner. */
export function akteExistingFolder(akte: { id?: string; nc_path?: string | null }): string {
  if (portalAkteNcPathIsForeign(akte)) return '';
  const pathAkteId = portalAkteIdFromNcPath(akte.nc_path);
  const ownAkteId = String(akte.id ?? '').trim();
  if (pathAkteId && (!ownAkteId || pathAkteId === ownAkteId)) {
    return portalAkteTargetFolder(pathAkteId);
  }
  return portalDocumentRelativeFolder(akte.nc_path ?? undefined)
    || sanitizePortalFolderInput(akte.nc_path ?? '');
}

export function akteFolderFromTitle(title: string): string {
  return sanitizePortalFolderInput(title.replace(/[\\/]+/g, ' '));
}

/** Vorhandener eigener Ordner, sonst `akten/<id>` — nie der Pfad einer anderen Akte. */
export function resolveAkteFolder(akte: { id?: string; title: string; nc_path?: string | null }): string {
  const existing = akteExistingFolder(akte);
  if (existing) return existing;
  const ownId = (akte.id || '').trim();
  if (ownId) return sanitizePortalFolderInput(`akten/${ownId}`);
  return akteFolderFromTitle(akte.title);
}

export function buildPortalAkteNcPath(kanzleiId: string, akteId: string): string {
  const kid = kanzleiId.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 80);
  const aid = akteId.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
  return `/Mandanten/${kid}/akten/${aid}/`;
}


export function buildPortalAktenChecklistPbFilter(customerId: string, akteId: string): string {
  const cid = escapePortalAktenPbFilterString(customerId);
  const aid = escapePortalAktenPbFilterString(akteId);
  return `(customer_id='${cid}' && akte_id='${aid}')`;
}

export function mapPbRecordToPortalAkte(r: Record<string, unknown>): PortalAkteRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;

  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';

  return {
    id,
    customer_id: read('customer_id'),
    kanzlei_id: read('kanzlei_id') || undefined,
    aktenfuehrer_id: read('aktenfuehrer_id') || undefined,
    matter_type: read('matter_type'),
    template_id: read('template_id'),
    title: read('title'),
    nc_path: read('nc_path') || undefined,
    mandant_label: read('mandant_label') || undefined,
    gegenseite_label: read('gegenseite_label') || undefined,
    vertragstyp: read('vertragstyp') || undefined,
    status: read('status') || 'in_progress',
    metadata_json: read('metadata_json') || undefined,
    external_key: read('external_key') || undefined,
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

export function mapPbRecordToPortalAktenChecklistItem(
  r: Record<string, unknown>,
): PortalAktenChecklistRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;

  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';

  const statusRaw = read('status') as PortalAktenChecklistStatus;
  const status: PortalAktenChecklistStatus =
    statusRaw === 'done' || statusRaw === 'waived' ? statusRaw : 'open';

  return {
    id,
    customer_id: read('customer_id'),
    akte_id: read('akte_id'),
    item_id: read('item_id'),
    label: read('label'),
    required: Boolean(r.required),
    sort_order: typeof r.sort_order === 'number' ? r.sort_order : Number(r.sort_order) || 0,
    status,
    note: read('note') || undefined,
    completed_at: read('completed_at') || undefined,
    completed_by: read('completed_by') || undefined,
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

async function pbFetchJson<T>(
  authToken: string,
  path: string,
  init?: RequestInit,
): Promise<PortalAktenApiResult<T>> {
  const token = normalizePbAuthHeader(authToken);
  if (!token) return { ok: false, error: 'Nicht angemeldet — bitte neu einloggen.' };

  const pbUrl = getPortalPocketBaseUrl().replace(/\/$/, '');
  try {
    const res = await fetch(`${pbUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        Authorization: token,
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : `HTTP ${res.status}`;
      const formatted = formatPortalAktenPbError(res.status, msg);
      if (formatted.collectionMissing) {
        portalAktenCollectionMissing = true;
      }
      const method = String(init?.method || 'GET').toUpperCase();
      const quietKanzleiPatch =
        method === 'PATCH' && isExpectedPortalAktenKanzleiDeny(res.status, msg);
      if (!quietKanzleiPatch) {
        logPortalError(`portal_akten HTTP ${res.status}`, msg);
      }
      return {
        ok: false,
        error: formatted.error,
        ...(formatted.collectionMissing ? { code: 'collection_missing' as const } : {}),
      };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, error: 'Verbindung zu PocketBase fehlgeschlagen.' };
  }
}

export async function listPortalAktenForCustomer(
  customerId: string,
  authToken: string,
  kanzleiId?: string,
): Promise<PortalAkteRecord[]> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildPortalAktenPbFilter(customerId, kanzleiId));
  const sort = encodeURIComponent(PORTAL_AKTEN_PB_LIST_SORT);
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records?filter=${filter}&sort=${sort}&perPage=100`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAkte)
    .filter((x): x is PortalAkteRecord => Boolean(x));
}

export async function findPortalAkteForPath(
  customerId: string,
  authToken: string,
  folderOrDocPath?: string | null,
  kanzleiId?: string,
): Promise<PortalAkteRecord | null> {
  const all = await listPortalAktenForCustomer(customerId, authToken, kanzleiId);
  if (!folderOrDocPath?.trim()) return null;
  const byIdOrOwn = findPortalAkteRecordForPath(all, folderOrDocPath);
  if (byIdOrOwn) return byIdOrOwn;
  // Lokale Bindung — Re-Export allein ist zur Laufzeit undefined (ReferenceError).
  return all.find((a) =>
    !portalAkteNcPathIsForeign(a) && matchesPortalAkteNcPath(a.nc_path, folderOrDocPath),
  ) ?? null;
}

export async function listChecklistItemsForAkte(
  customerId: string,
  authToken: string,
  akteId: string,
): Promise<PortalAktenChecklistRecord[]> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildPortalAktenChecklistPbFilter(customerId, akteId));
  const sort = encodeURIComponent(PORTAL_AKTEN_CHECKLIST_SORT);
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records?filter=${filter}&sort=${sort}&perPage=200`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAktenChecklistItem)
    .filter((x): x is PortalAktenChecklistRecord => Boolean(x));
}

export async function updateChecklistItemStatus(
  authToken: string,
  recordId: string,
  status: PortalAktenChecklistStatus,
  completedBy?: string,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const now = new Date().toISOString();
  const body: Record<string, unknown> = { status };
  if (status === 'done' || status === 'waived') {
    body.completed_at = now;
    if (completedBy?.trim()) body.completed_by = completedBy.trim();
  } else {
    body.completed_at = '';
    body.completed_by = '';
  }

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

/** Nur der Text. Leerer Name wird nicht geschrieben. */
export async function updateChecklistItemLabel(
  authToken: string,
  recordId: string,
  label: string,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  const trimmed = label.trim();
  if (!trimmed) return { ok: false, error: 'Bezeichnung fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'PATCH', body: JSON.stringify({ label: trimmed }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

/** Bestehendes Notizfeld. Leer nimmt den Favorit weg. */
export async function updateChecklistItemNote(
  authToken: string,
  recordId: string,
  note: string,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  const trimmed = note.trim();
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'PATCH', body: JSON.stringify({ note: trimmed }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function addChecklistItemToAkte(
  customerId: string,
  authToken: string,
  akteId: string,
  label: string,
  required = false,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  const trimmed = label.trim();
  if (!trimmed) return { ok: false, error: 'Bezeichnung fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }

  const existing = await listChecklistItemsForAkte(customerId, authToken, akteId);
  const maxOrder = existing.reduce((m, row) => Math.max(m, row.sort_order), 0);
  const slugBase = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  const itemId = `custom_${slugBase || 'punkt'}_${Date.now().toString(36)}`;

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        akte_id: akteId,
        item_id: itemId,
        label: trimmed,
        required,
        sort_order: maxOrder + 1,
        status: 'open',
      }),
    },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

/** Vorlage-Punkt an genau dieser Akte speichern. Leerer Name wird nicht geschrieben. */
export async function createChecklistItemForAkte(
  customerId: string,
  authToken: string,
  input: {
    akteId: string;
    itemId: string;
    label: string;
    required?: boolean;
    sortOrder?: number;
    status?: PortalAktenChecklistStatus;
    note?: string;
  },
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  const label = input.label.trim();
  const akteId = input.akteId.trim();
  const itemId = input.itemId.trim();
  if (!label) return { ok: false, error: 'Bezeichnung fehlt.' };
  if (!akteId || !itemId) return { ok: false, error: 'Akte oder Punkt fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        akte_id: akteId,
        item_id: itemId,
        label,
        required: Boolean(input.required),
        sort_order: input.sortOrder ?? 0,
        status: input.status ?? 'open',
        ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      }),
    },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped || mapped.akte_id !== akteId) {
    return { ok: false, error: 'Punkt gehört nicht zu dieser Akte.' };
  }
  return { ok: true, data: mapped };
}

export async function removeChecklistItemFromAkte(
  authToken: string,
  recordId: string,
): Promise<PortalAktenApiResult<null>> {
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const result = await pbFetchJson<unknown>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'DELETE' },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  return { ok: true, data: null };
}

export interface CreatePortalAkteInput {
  title: string;
  nc_path?: string;
  kanzlei_id?: string;
  mandant_label?: string;
  gegenseite_label?: string;
  vertragstyp?: string;
  matter_type?: string;
  template_id?: string;
  /** Nur diese Template-Punkte seeden (leer = alle). */
  checklist_item_ids?: string[];
}

/** Neue Akte Vertrag/Beratung anlegen + Checkliste aus Template seeden. */
export async function createPortalAkteVertragBeratung(
  customerId: string,
  authToken: string,
  input: CreatePortalAkteInput,
): Promise<PortalAktenApiResult<{ akte: PortalAkteRecord; checklistCount: number }>> {
  const title = input.title.trim();
  if (!title) return { ok: false, error: 'Akten-Titel fehlt.' };

  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }

  const templateId = input.template_id?.trim() || VERTRAG_BERATUNG_CHECKLIST.template_id;
  const matterType = input.matter_type?.trim() || VERTRAG_BERATUNG_CHECKLIST.matter_type;
  const template = getAktenChecklistTemplate(templateId) ?? VERTRAG_BERATUNG_CHECKLIST;

  const ncPath = input.nc_path?.trim()
    ? input.nc_path.trim().endsWith('/')
      ? input.nc_path.trim()
      : `${input.nc_path.trim()}/`
    : '';

  const akteResult = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        kanzlei_id: input.kanzlei_id?.trim() || undefined,
        aktenfuehrer_id: customerId,
        matter_type: matterType,
        template_id: templateId,
        title,
        nc_path: ncPath || undefined,
        mandant_label: input.mandant_label?.trim() || undefined,
        gegenseite_label: input.gegenseite_label?.trim() || undefined,
        vertragstyp: input.vertragstyp?.trim() || undefined,
        status: 'in_progress',
        metadata_json: JSON.stringify({
          scenario: 'portal-guided-phase1',
          checklist_template: templateId,
          seeded_at: new Date().toISOString(),
        }),
      }),
    },
  );
  if (akteResult.ok === false) {
    return { ok: false, error: akteResult.error, code: akteResult.code };
  }

  let akte = mapPbRecordToPortalAkte(akteResult.data);
  if (!akte) return { ok: false, error: 'Akte konnte nicht gelesen werden.' };

  const kid = (akte.kanzlei_id || input.kanzlei_id || '').trim();
  if (kid && !akte.nc_path) {
    const patched = await updatePortalAkteNcPath(
      authToken,
      akte.id,
      buildPortalAkteNcPath(kid, akte.id),
    );
    if (patched.ok) akte = patched.data;
  }

  let checklistCount = 0;
  const selectedIds = input.checklist_item_ids?.length
    ? new Set(input.checklist_item_ids)
    : null;
  const templateItems = [...template.items]
    .sort((a, b) => a.order - b.order)
    .filter((item) => !selectedIds || selectedIds.has(item.id));

  if (!templateItems.length) {
    return { ok: false, error: 'Mindestens ein Checklisten-Punkt muss gewählt sein.' };
  }

  for (const item of templateItems) {
    const rowResult = await pbFetchJson<Record<string, unknown>>(
      authToken,
      `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records`,
      {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          kanzlei_id: input.kanzlei_id?.trim() || undefined,
          akte_id: akte.id,
          item_id: item.id,
          label: item.label,
          required: item.required,
          sort_order: item.order,
          status: 'open',
        }),
      },
    );
    if (rowResult.ok) checklistCount += 1;
  }

  if (checklistCount === 0) {
    return { ok: false, error: 'Akte angelegt, Checkliste konnte nicht gespeichert werden.' };
  }

  return { ok: true, data: { akte, checklistCount } };
}

/** NC-Ordner/Pfad einer bestehenden Akte aktualisieren (Drag & Drop Verknüpfung). */
export async function updatePortalAkteNcPath(
  authToken: string,
  akteId: string,
  ncPath: string,
): Promise<PortalAktenApiResult<PortalAkteRecord>> {
  const trimmed = ncPath.trim();
  if (!trimmed) return { ok: false, error: 'Pfad fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const normalized = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records/${akteId}`,
    { method: 'PATCH', body: JSON.stringify({ nc_path: normalized }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAkte(result.data);
  if (!mapped) return { ok: false, error: 'Akte konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function updatePortalAkteTitle(
  authToken: string,
  akteId: string,
  title: string,
): Promise<PortalAktenApiResult<PortalAkteRecord>> {
  const trimmed = title.trim();
  if (!trimmed) return { ok: false, error: 'Akten-Titel fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records/${akteId}`,
    { method: 'PATCH', body: JSON.stringify({ title: trimmed }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mappedTitle = mapPbRecordToPortalAkte(result.data);
  if (!mappedTitle) return { ok: false, error: 'Akte konnte nicht gelesen werden.' };
  return { ok: true, data: mappedTitle };
}

export function mapPbRecordToPortalAkteMember(
  r: Record<string, unknown>,
): PortalAkteMemberRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;
  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';
  const accessRaw = read('access');
  return {
    id,
    akte_id: read('akte_id'),
    kanzlei_id: read('kanzlei_id'),
    customer_id: read('customer_id'),
    access: accessRaw === 'write' ? 'write' : 'read',
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

export function buildPortalAkteMembersPbFilter(akteId: string, kanzleiId: string): string {
  const aid = escapePbFilterString(akteId);
  const kid = escapePbFilterString(kanzleiId);
  return `(akte_id='${aid}' && kanzlei_id='${kid}')`;
}

export async function listPortalAkteMembers(
  authToken: string,
  akteId: string,
  kanzleiId: string,
): Promise<PortalAkteMemberRecord[]> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildPortalAkteMembersPbFilter(akteId, kanzleiId));
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_MEMBERS_COLLECTION}/records?filter=${filter}&sort=id&perPage=100`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAkteMember)
    .filter((x): x is PortalAkteMemberRecord => Boolean(x));
}

export async function addPortalAkteMember(
  authToken: string,
  input: {
    akte_id: string;
    kanzlei_id: string;
    customer_id: string;
    access: PortalAkteMemberAccess;
  },
): Promise<PortalAktenApiResult<PortalAkteMemberRecord>> {
  if (skipPortalAktenPbFetch()) {
    return { ok: false, error: PORTAL_AKTEN_COLLECTION_MISSING_DE, code: 'collection_missing' };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_MEMBERS_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        akte_id: input.akte_id,
        kanzlei_id: input.kanzlei_id,
        customer_id: input.customer_id,
        access: input.access === 'write' ? 'write' : 'read',
      }),
    },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAkteMember(result.data);
  if (!mapped) return { ok: false, error: 'Mitgliedschaft konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function updatePortalAkteMemberAccess(
  authToken: string,
  recordId: string,
  access: PortalAkteMemberAccess,
): Promise<PortalAktenApiResult<PortalAkteMemberRecord>> {
  if (skipPortalAktenPbFetch()) {
    return { ok: false, error: PORTAL_AKTEN_COLLECTION_MISSING_DE, code: 'collection_missing' };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_MEMBERS_COLLECTION}/records/${recordId}`,
    { method: 'PATCH', body: JSON.stringify({ access: access === 'write' ? 'write' : 'read' }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAkteMember(result.data);
  if (!mapped) return { ok: false, error: 'Mitgliedschaft konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function removePortalAkteMember(
  authToken: string,
  recordId: string,
): Promise<PortalAktenApiResult<null>> {
  if (skipPortalAktenPbFetch()) {
    return { ok: false, error: PORTAL_AKTEN_COLLECTION_MISSING_DE, code: 'collection_missing' };
  }
  const result = await pbFetchJson<unknown>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_MEMBERS_COLLECTION}/records/${recordId}`,
    { method: 'DELETE' },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  return { ok: true, data: null };
}

export function buildMyPortalAkteMembersPbFilter(customerId: string, kanzleiId: string): string {
  const cid = escapePbFilterString(customerId);
  const kid = escapePbFilterString(kanzleiId);
  return `(customer_id='${cid}' && kanzlei_id='${kid}')`;
}

/** Akte-IDs, in denen der angemeldete Sitz Mitglied ist (Server filtert via Rules). */
export async function listMyPortalAkteMemberAkteIds(
  authToken: string,
  customerId: string,
  kanzleiId: string,
): Promise<string[] | null> {
  const rows = await listMyPortalAkteMembers(authToken, customerId, kanzleiId);
  if (rows === null) return null;
  const ids = new Set<string>();
  for (const mapped of rows) {
    if (mapped.akte_id) ids.add(mapped.akte_id);
  }
  return Array.from(ids);
}

/** Mitglieds-Records des angemeldeten Sitzes (inkl. created), ohne fremde Sitze. */
export async function listMyPortalAkteMembers(
  authToken: string,
  customerId: string,
  kanzleiId: string,
): Promise<PortalAkteMemberRecord[] | null> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildMyPortalAkteMembersPbFilter(customerId, kanzleiId));
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_MEMBERS_COLLECTION}/records?filter=${filter}&perPage=200`,
  );
  if (!result.ok) return null;
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAkteMember)
    .filter((x): x is PortalAkteMemberRecord => Boolean(x) && x.customer_id === customerId.trim());
}

export async function listKanzleiSeatsForAktePicker(
  authToken: string,
  kanzleiId: string,
): Promise<KanzleiSeatForAktePicker[]> {
  const kid = escapePbFilterString(kanzleiId);
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/customers/records?filter=${encodeURIComponent(`(kanzlei_id='${kid}')`)}&sort=email&perPage=100`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map((r): KanzleiSeatForAktePicker | null => {
      const id = typeof r.id === 'string' ? r.id : '';
      const email = typeof r.email === 'string' ? r.email : '';
      if (!id) return null;
      const seat: KanzleiSeatForAktePicker = {
        id,
        email,
        name: typeof r.name === 'string' ? r.name : '',
        element_seat: readSeatElementEnabled(r),
        matrix_user_id: readSeatMatrixUserId(r),
      };
      if (typeof r.portal_role === 'string' && r.portal_role) {
        seat.portal_role = r.portal_role;
      }
      return seat;
    })
    .filter((x): x is KanzleiSeatForAktePicker => x !== null);
}

